import type { ErrorEvent, EventHint } from '@sentry/react';
import { getOpenTabCount } from '@frontend/tab-management';
import { tagThirdPartyEvent } from '@frontend/tracking';

type SentryEnvs = 'production' | 'debug';

export const sentryEnvironment: SentryEnvs =
  location.hostname.includes('f.weavedev.net') || location.hostname.includes('localhost') ? 'debug' : 'production';

/**
 * NOTE: This sample rate is applied in beforeSend. Sentry.init must set `sampleRate: 1`, otherwise
 * the SDK samples again after beforeSend and errors are double-sampled.
 */
export const DEFAULT_SAMPLE_RATE = sentryEnvironment === 'production' ? 0.25 : 0;

/**
 * Gets the HTTP status from errors thrown by gatekeeper's fetch wrapper and the auth lib's fetch.
 * Falls back to parsing the message for errors that were re-thrown without the status.
 */
export function getHttpStatus(error: unknown): number | undefined {
  if (error && typeof error === 'object' && 'status' in error && typeof error.status === 'number') {
    return error.status;
  }
  const message = error instanceof Error ? error.message : typeof error === 'string' ? error : undefined;
  const match = message?.match(/(?:HTTP error|Request failed with status) (\d{3})\b/i);
  return match ? Number(match[1]) : undefined;
}

/**
 * This function is used as the beforeSend callback in Sentry.init. It applies a sample rate to events in production, with an optional override at the event level.
 * If the function returns null, the event is dropped and not sent to Sentry.
 * If it returns the event, the event is sent to Sentry.
 */
export function beforeSend(event: ErrorEvent, hint: EventHint) {
  // Tag events originating from third-party scripts
  const taggedEvent = tagThirdPartyEvent(event);

  if (taggedEvent) {
    taggedEvent.tags = { ...taggedEvent.tags, openTabCount: String(getOpenTabCount()) };

    const httpStatus = getHttpStatus(hint?.originalException);
    if (httpStatus !== undefined) {
      taggedEvent.tags['http.status'] = String(httpStatus);
    }
    // 401s are expected (e.g. wrong password) and noisy, but we keep them as warnings to help trace customer issues
    if (httpStatus === 401) {
      taggedEvent.level = 'warning';
    }
  }

  // the event can override the default sample rate in production, otherwise it falls back to the default
  const eventSampleRate =
    sentryEnvironment === 'production' && typeof taggedEvent?.contexts?.meta?.sampleRate === 'number'
      ? taggedEvent.contexts.meta.sampleRate
      : undefined;
  const sampleRate = eventSampleRate ?? DEFAULT_SAMPLE_RATE;
  return Math.random() < sampleRate ? taggedEvent : null;
}
